import { useState, useEffect, useCallback, useRef } from 'react';
import supabase from '../utils/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { notificationManager } from '../components/Notification';

const useGameRoom = (roomCode) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [roomData, setRoomData] = useState(null);
  const [loading, setLoading] = useState(roomCode ? true : false);
  const [error, setError] = useState('');
  const [playerId, setPlayerId] = useState(null);
  const [isHost, setIsHost] = useState(false);
  
  // Use refs to store subscription objects to avoid re-renders
  const roomSubscriptionRef = useRef(null);
  const playerSubscriptionRef = useRef(null);
  const spinResultsSubscriptionRef = useRef(null);
  const hasJoinedRef = useRef(false); // Track if user has joined to prevent auto-cleanup

  // Create a reference for the broadcast channel
  const broadcastChannelRef = useRef(null);

  // Add error recovery state
  const [isRecovering, setIsRecovering] = useState(false);
  const recoveryAttempts = useRef(0);
  const maxRecoveryAttempts = 3;

  // Helper function to clean up subscriptions - MOVED UP to avoid reference error
  const cleanupSubscriptions = useCallback(() => {
    try {
      if (roomSubscriptionRef.current) {
        roomSubscriptionRef.current.unsubscribe();
        roomSubscriptionRef.current = null;
      }
      
      if (playerSubscriptionRef.current) {
        playerSubscriptionRef.current.unsubscribe();
        playerSubscriptionRef.current = null;
      }
      
      if (spinResultsSubscriptionRef.current) {
        spinResultsSubscriptionRef.current.unsubscribe();
        spinResultsSubscriptionRef.current = null;
      }
    } catch (err) {
      console.error('Error cleaning up subscriptions:', err);
    }
  }, []);

  // Check if the current player is the host with error handling
  const isRoomHost = useCallback(() => {
    try {
      return isHost;
    } catch (err) {
      console.error('Error checking if room host:', err);
      return false;
    }
  }, [isHost]);

  // Check if it's the current player's turn with comprehensive error handling
  const isMyTurn = useCallback(() => {
    try {
      if (!roomData || !playerId) {
        console.log('Turn check failed: missing roomData or playerId', { roomData: !!roomData, playerId });
        return false;
      }
      
      // If there are no players or current_player_index is invalid, return false
      if (!roomData.players || !Array.isArray(roomData.players) || roomData.players.length === 0) {
        console.log('Turn check failed: no players in room or invalid players array');
        return false;
      }
      
      // If wheel is spinning, no one's turn
      if (roomData.is_wheel_spinning) {
        return false;
      }
      
      // Handle case where current_player_index might be null/undefined
      const currentIndex = roomData.current_player_index;
      if (currentIndex === null || currentIndex === undefined || currentIndex < 0) {
        console.log('Turn check failed: invalid current_player_index (null/undefined/negative)', { currentIndex });
        // Default to first player if index is invalid
        return playerId === roomData.players[0]?.id;
      }
      
      // Handle case where index is out of bounds
      if (currentIndex >= roomData.players.length) {
        console.log('Turn check failed: current_player_index out of bounds', { 
          currentIndex, 
          playersLength: roomData.players.length 
        });
        // Reset to first player if index is out of bounds
        return playerId === roomData.players[0]?.id;
      }
      
      // Get the current player
      const currentPlayer = roomData.players[currentIndex];
      if (!currentPlayer) {
        console.log('Turn check failed: no player at current index', { currentIndex });
        return false;
      }
      
      // Compare player IDs
      const currentPlayerId = currentPlayer.id;
      const isMyTurnResult = currentPlayerId === playerId;
      
      // Debug logging for turn management - only log when turn status changes
      const turnState = {
        myPlayerId: playerId,
        currentPlayerIndex: currentIndex,
        currentPlayerId: currentPlayerId,
        currentPlayerName: currentPlayer.player_name || currentPlayer.name,
        isMyTurn: isMyTurnResult,
        totalPlayers: roomData.players.length,
        wheelSpinning: roomData.is_wheel_spinning
      };
      
      // Only log significant changes to reduce console spam
      if (!window.lastTurnState || 
          window.lastTurnState?.isMyTurn !== isMyTurnResult || 
          window.lastTurnState?.currentPlayerIndex !== currentIndex ||
          window.lastTurnState?.wheelSpinning !== roomData.is_wheel_spinning) {
        console.log('Turn status:', turnState);
        window.lastTurnState = turnState;
      }
      
      return isMyTurnResult;
    } catch (err) {
      console.error('Error checking if my turn:', err);
      return false;
    }
  }, [roomData, playerId]);

  // Fetch room data with enhanced error handling
  const fetchRoomData = useCallback(async (retryCount = 0, maxRetries = 3, setLoadingState = true) => {
    if (!roomCode) {
      console.error('No room code provided to fetchRoomData');
      return false;
    }
    
    try {
      // Only set loading state for initial loads or manual refreshes
      if (setLoadingState) {
        setLoading(true);
      }
      
      console.log('Fetching room data for room code:', roomCode);
      
      // Add connection check
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }
      
      // Simplified query with timeout
      const fetchPromise = supabase
        .from('game_rooms')
        .select(`
          *,
          players(*)
        `)
        .eq('room_code', roomCode)
        .single();
        
      // Add a timeout to prevent hanging requests
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 15000) // Increased to 15 seconds
      );
      
      // Race between the fetch and the timeout
      const result = await Promise.race([
        fetchPromise,
        timeoutPromise
      ]);
      
      // Handle timeout case
      if (result instanceof Error) {
        throw result;
      }
      
      const { data: roomData, error: roomError } = result;
      
      if (roomError) {
        console.error('Supabase error fetching room data:', roomError);
        
        // Special handling for "room not found" errors
        if (roomError.code === 'PGRST116' || roomError.message?.includes('No rows')) {
          // Check if this might be a newly created room by looking for temp host ID
          const tempHostId = localStorage.getItem(`temp_host_${roomCode}`);
          if (tempHostId && retryCount < maxRetries) {
            console.log(`Room not found but temp host ID exists, retrying... (attempt ${retryCount + 1}/${maxRetries + 1})`);
            // For newly created rooms, use a shorter retry delay
            setTimeout(() => {
              fetchRoomData(retryCount + 1, maxRetries, setLoadingState)
                .catch(e => {
                  console.error('Error in newly created room retry:', e);
                  if (setLoadingState) {
                    setLoading(false);
                  }
                });
            }, 500); // 500ms delay for newly created rooms
            return false;
          }
        }
        
        throw roomError;
      }
      
      if (!roomData) {
        const tempHostId = localStorage.getItem(`temp_host_${roomCode}`);
        if (tempHostId && retryCount < maxRetries) {
          console.log(`Room data is null but temp host ID exists, retrying... (attempt ${retryCount + 1}/${maxRetries + 1})`);
          setTimeout(() => {
            fetchRoomData(retryCount + 1, maxRetries, setLoadingState)
              .catch(e => {
                console.error('Error in null room data retry:', e);
                if (setLoadingState) {
                  setLoading(false);
                }
              });
          }, 500);
          return false;
        }
        
        setError('Room not found');
        if (setLoadingState) {
          setLoading(false);
        }
        return false;
      }
      
      // Validate room data structure
      if (!roomData.id || !roomData.room_code) {
        console.error('Invalid room data structure:', roomData);
        throw new Error('Invalid room data received from server');
      }
      
      // Ensure players array exists
      if (!roomData.players) {
        roomData.players = [];
      }
      
      // Validate players array
      if (!Array.isArray(roomData.players)) {
        console.error('Players data is not an array:', roomData.players);
        roomData.players = [];
      }
      
      // Set the room data
      setRoomData(roomData);
      
      // Check if current player is the host
      try {
        const savedPlayerId = localStorage.getItem(`player_${roomCode}`);
        if (savedPlayerId && roomData.host_player_id === savedPlayerId) {
          setIsHost(true);
        } else {
          setIsHost(false);
        }
        
        // If we have a saved player ID, mark as joined
        if (savedPlayerId) {
          hasJoinedRef.current = true;
          setPlayerId(savedPlayerId);
        }
      } catch (err) {
        console.error('Error checking if room host:', err);
        setIsHost(false);
      }
      
      setError('');
      if (setLoadingState) {
        setLoading(false);
      }
      
      // Reset recovery attempts on successful fetch
      recoveryAttempts.current = 0;
      setIsRecovering(false);
      
      return true;
    } catch (err) {
      console.error(`Error fetching room data (attempt ${retryCount+1}/${maxRetries+1}):`, err);
      
      // Handle all errors with retry logic
      if (retryCount < maxRetries) {
        // Exponential backoff: 1s, 2s, 4s, etc.
        const delay = Math.pow(2, retryCount) * 1000;
        console.log(`Retrying after ${delay}ms...`);
        
        // Only show the error message if we're on the last retry
        if (retryCount === maxRetries - 1) {
          setError('Error connecting to the server. Retrying...');
        }
        
        // Wait for the backoff delay and retry
        setTimeout(() => {
          fetchRoomData(retryCount + 1, maxRetries, setLoadingState)
            .catch(e => {
              console.error('Error in retry:', e);
              if (setLoadingState) {
                setLoading(false);
              }
            });
        }, delay);
        
        return false; // Exit function, we'll retry later
      } else {
        // We've exhausted our retries
        const errorMessage = err.message?.includes('timeout') 
          ? 'Connection timeout. Please check your internet connection and try again.'
          : err.message?.includes('Network')
          ? 'Network error. Please check your connection and try again.'
          : err.message?.includes('Invalid room data')
          ? 'Invalid room data received. Please refresh the page and try again.'
          : 'Error connecting to the server. Please try refreshing the page.';
        
        setError(errorMessage);
      }
      
      if (setLoadingState) {
        setLoading(false);
      }
      return false;
    }
  }, [roomCode, setLoading, setError, setRoomData, setIsHost, setPlayerId, setIsRecovering]);

  // Enhanced error recovery mechanism
  const attemptRecovery = useCallback(async () => {
    if (isRecovering || recoveryAttempts.current >= maxRecoveryAttempts) {
      return false;
    }

    try {
      setIsRecovering(true);
      recoveryAttempts.current += 1;
      
      console.log(`Attempting recovery (${recoveryAttempts.current}/${maxRecoveryAttempts})`);
      
      // Clean up existing subscriptions
      cleanupSubscriptions();
      
      // Wait a bit before retrying
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Try to fetch room data again
      const success = await fetchRoomData(0, 2, false);
      
      if (success) {
        console.log('Recovery successful');
        return true;
      } else {
        throw new Error('Failed to recover room data');
      }
    } catch (err) {
      console.error('Recovery attempt failed:', err);
      return false;
    } finally {
      setIsRecovering(false);
    }
  }, [isRecovering, cleanupSubscriptions, fetchRoomData]);

  // Add global error handler for unhandled promise rejections
  useEffect(() => {
    const handleUnhandledRejection = (event) => {
      console.error('Unhandled promise rejection in useGameRoom:', event.reason);
      
      // Safely check if it's a network error before attempting recovery
      const errorMessage = event.reason?.message || '';
      const isNetworkError = errorMessage.includes('Network') || 
                            errorMessage.includes('fetch') ||
                            errorMessage.includes('timeout') ||
                            errorMessage.includes('ERR_NAME_NOT_RESOLVED') ||
                            errorMessage.includes('ERR_CONNECTION_REFUSED');
      
      if (isNetworkError) {
        console.log('Network error detected, attempting recovery...');
        attemptRecovery();
      } else {
        console.log('Non-network error, not attempting recovery:', errorMessage);
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [attemptRecovery]);

  // Create a new room with retry logic and enhanced error handling
  const createRoom = async (retryCount = 0, maxRetries = 2) => {
    // Prevent multiple simultaneous creation attempts
    if (loading) {
      console.log('Room creation already in progress, skipping...');
      return null;
    }
    
    console.log('Starting room creation process...');
    setLoading(true);
    setError('');
    
    try {
      // Check room limits for the user
      if (user) {
        const { data: existingRooms, error: roomCountError } = await supabase
          .from('game_rooms')
          .select('id, is_active')
          .eq('host_user_id', user.id)
          .eq('is_active', true);
        
        if (roomCountError) {
          console.error('Error checking existing rooms:', roomCountError);
        } else {
          const activeRoomCount = existingRooms?.length || 0;
          
          // Premium users can have up to 6 active rooms, free users unlimited but with auto-deletion
          if (user.is_premium && activeRoomCount >= 6) {
            const errorMessage = t('roomLimitReached');
            if (typeof notificationManager?.error === 'function') {
              notificationManager.error(errorMessage, {
                title: 'Room Limit Reached',
                duration: 8000,
                actions: [
                  {
                    label: 'Manage Rooms',
                    onClick: () => {
                      // This will be handled by the component that uses this hook
                      window.location.href = '/manage-rooms';
                    }
                  }
                ]
              });
            }
            throw new Error(errorMessage);
          }
          
          console.log(`User has ${activeRoomCount} active rooms`);
        }
      }
      
      console.log('Attempting to create new room...');
      
      // Generate a simple uppercase alphanumeric room code
      const generatedRoomCode = Math.random().toString(36).substring(2, 8).toUpperCase();
      console.log('Generated room code:', generatedRoomCode);
      
      // Generate a temporary host ID
      const tempHostId = 'temp_' + Date.now();
      
      // Create basic default data without complex objects
      const roomData = {
        room_code: generatedRoomCode,
        host_player_id: tempHostId,
        host_user_id: user?.id || null, // Link to authenticated user
        current_player_index: 0, // Initialize turn to first player
        is_active: true, // Mark room as active
        wheel_segments: [
          { text: 'Take a shot', color: 'var(--color-primary)' },
          { text: 'Give a drink', color: 'var(--color-secondary)' },
          { text: 'Truth or Dare', color: 'var(--color-accent)' },
          { text: 'Skip your turn', color: 'var(--color-dark)' },
          { text: 'Everyone drinks', color: 'var(--color-primary)' },
          { text: 'Make a rule', color: 'var(--color-secondary)' }
        ],
        game_settings: {
          wheelSize: 'medium',
          allowPlayersToSpin: true,
          wheelTheme: 'classic'
        },
        max_segments: user ? (user.is_premium ? -1 : 6) : 6 // Premium users get unlimited segments
      };
      
      console.log('Inserting room with data:', generatedRoomCode);
      
      // Insert the room data
      const { error: insertError } = await supabase
        .from('game_rooms')
        .insert(roomData);
      
      if (insertError) {
        console.error('Error creating room:', insertError);
        throw new Error(`Failed to create room: ${insertError.message}`);
      }
      
      console.log('Room inserted successfully, verifying...');
      
      // Verify the room was created by trying to fetch it
      let verificationAttempts = 0;
      const maxVerificationAttempts = 5;
      
      while (verificationAttempts < maxVerificationAttempts) {
        try {
          const { data: verificationData, error: verificationError } = await supabase
            .from('game_rooms')
            .select('id, room_code')
            .eq('room_code', generatedRoomCode)
            .single();
            
          if (!verificationError && verificationData) {
            console.log('Room creation verified successfully:', verificationData);
            break;
          }
          
          console.log(`Verification attempt ${verificationAttempts + 1} failed, retrying...`);
          verificationAttempts++;
          
          if (verificationAttempts < maxVerificationAttempts) {
            // Wait a bit before retrying verification
            await new Promise(resolve => setTimeout(resolve, 200));
          }
        } catch (err) {
          console.error('Error during room verification:', err);
          verificationAttempts++;
          
          if (verificationAttempts < maxVerificationAttempts) {
            await new Promise(resolve => setTimeout(resolve, 200));
          }
        }
      }
      
      if (verificationAttempts >= maxVerificationAttempts) {
        throw new Error('Room created but could not be verified. Please try again.');
      }
      
      // Store the temp host ID
      localStorage.setItem(`temp_host_${generatedRoomCode}`, tempHostId);
      
      // Clear loading state
      setLoading(false);
      
      console.log('Room creation completed successfully:', generatedRoomCode);
      return generatedRoomCode;
    } catch (err) {
      console.error('Error in createRoom:', err);
      setError(err.message || 'Failed to create room');
      setLoading(false);
      return null;
    }
  };

  // Join a room as a player
  const joinRoom = async (playerName, avatarUrl, password = null) => {
    console.log('Attempting to join room:', roomCode, 'with name:', playerName);
    
    if (!roomCode || !playerName) {
      console.error('Missing required parameters for joining room');
      setError('Missing room code or player name');
      return false;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Add connection timeout for the entire join process
      const joinTimeout = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Connection timeout. Please check your internet connection and try again.')), 30000)
      );

      // Check if the room exists first with timeout
      const roomCheckPromise = supabase
        .from('game_rooms')
        .select('id, room_code, host_player_id, is_private')
        .eq('room_code', roomCode)
        .single();

      const { data: roomResult, error: roomError } = await Promise.race([
        roomCheckPromise,
        joinTimeout
      ]);
      
      if (roomError || !roomResult) {
        console.error('Room not found:', roomError);
        if (roomError?.code === 'PGRST116') {
          throw new Error(`Room with code ${roomCode} not found. Please check the room code and try again.`);
        } else if (roomError?.message?.includes('timeout')) {
          throw new Error('Connection timeout. Please check your internet connection and try again.');
        } else {
          throw new Error(`Room with code ${roomCode} not found`);
        }
      }
      
      console.log('Found room with ID:', roomResult.id);
      
      // Verify password for private rooms
      if (roomResult.is_private && password) {
        try {
          const { data: passwordCheck, error: passwordError } = await supabase
            .from('game_rooms')
            .select('id')
            .eq('room_code', roomCode)
            .eq('room_password', password)
            .single();
            
          if (passwordError || !passwordCheck) {
            throw new Error('Incorrect password for this room');
          }
        } catch (err) {
          if (err.message === 'Incorrect password for this room') {
            throw err;
          }
          throw new Error('Failed to verify room password. Please try again.');
        }
      }
      
      // Check if player already exists in local storage
      const existingPlayerId = localStorage.getItem(`player_${roomCode}`);
      
      if (existingPlayerId) {
        console.log('Player already joined with ID:', existingPlayerId);
        
        // Check if player exists in database with timeout
        const playerCheckPromise = supabase
          .from('players')
          .select('*')
          .eq('id', existingPlayerId)
          .single();

        const { data: existingPlayer, error: playerCheckError } = await Promise.race([
          playerCheckPromise,
          joinTimeout
        ]);
          
        if (playerCheckError || !existingPlayer) {
          console.log('Stored player ID not found in database, creating new player');
          // Player doesn't exist, need to create a new one
          localStorage.removeItem(`player_${roomCode}`);
        } else {
          // Update existing player
          console.log('Updating existing player:', existingPlayer);
          setPlayerId(existingPlayerId);
          
          // Update connection status and name if needed
          const updatePromise = supabase
            .from('players')
            .update({ 
              player_name: playerName, 
              avatar_url: avatarUrl || existingPlayer.avatar_url, 
              is_connected: true,
              last_active: new Date().toISOString()
            })
            .eq('id', existingPlayerId);

          await Promise.race([updatePromise, joinTimeout]);
            
          // Check if this is the host
          if (roomResult.host_player_id === existingPlayerId) {
            setIsHost(true);
          }
          
          await fetchRoomData();
          setLoading(false);
          return true;
        }
      }
      
      // Create a new player with the correct game_room_id
      const createPlayerPromise = supabase
        .from('players')
        .insert({
          game_room_id: roomResult.id, // Make sure to use the room ID from the query
          player_name: playerName,
          avatar_url: avatarUrl || null,
          is_connected: true,
          joined_at: new Date().toISOString(),
          last_active: new Date().toISOString()
        })
        .select()
        .single();

      const { data: newPlayer, error: playerError } = await Promise.race([
        createPlayerPromise,
        joinTimeout
      ]);
      
      if (playerError) {
        console.error('Error creating player:', playerError);
        if (playerError.code === '23505') { // Unique constraint violation
          throw new Error('A player with this name already exists in the room. Please choose a different name.');
        } else if (playerError.message?.includes('timeout')) {
          throw new Error('Connection timeout while creating player. Please try again.');
        } else {
          throw new Error(`Failed to join room: ${playerError.message}`);
        }
      }
      
      console.log('Player created:', newPlayer);
      
      // Store player ID in local storage
      localStorage.setItem(`player_${roomCode}`, newPlayer.id);
      setPlayerId(newPlayer.id);
      
      // If this is the host player, update the host_player_id
      if (roomResult.host_player_id && roomResult.host_player_id.startsWith('temp_')) {
        const tempHostId = localStorage.getItem(`temp_host_${roomCode}`);
        if (tempHostId && tempHostId === roomResult.host_player_id) {
          console.log('Updating host player ID');
          const updateHostPromise = supabase
            .from('game_rooms')
            .update({ host_player_id: newPlayer.id })
            .eq('id', roomResult.id);

          await Promise.race([updateHostPromise, joinTimeout]);
          
          setIsHost(true);
        }
      }
      
      // Ensure current_player_index is properly set for the game
      const roomDataPromise = supabase
        .from('game_rooms')
        .select('current_player_index, players(*)')
        .eq('id', roomResult.id)
        .single();

      const { data: updatedRoomData } = await Promise.race([
        roomDataPromise,
        joinTimeout
      ]);
        
      // If current_player_index is null or undefined, initialize it to 0
      if (updatedRoomData && (updatedRoomData.current_player_index === null || updatedRoomData.current_player_index === undefined)) {
        console.log('Initializing current_player_index to 0');
        const initPromise = supabase
          .from('game_rooms')
          .update({ current_player_index: 0 })
          .eq('id', roomResult.id);

        await Promise.race([initPromise, joinTimeout]);
      }
      
      // Broadcast to other tabs/windows that a player joined
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.postMessage({
          type: 'PLAYER_JOINED',
          playerId: newPlayer.id,
          playerName
        });
      }
      
      // Force update right after joining
      await fetchRoomData();
      setLoading(false);
      return true;
    } catch (error) {
      console.error('Error joining room:', error);
      
      // Provide more specific error messages
      let errorMessage = error.message;
      
      if (error.message?.includes('timeout')) {
        errorMessage = 'Connection timeout. Please check your internet connection and try again.';
      } else if (error.message?.includes('Network')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.message?.includes('fetch')) {
        errorMessage = 'Failed to connect to the server. Please try again.';
      } else if (error.message?.includes('Incorrect password')) {
        errorMessage = 'Incorrect password for this room. Please try again.';
      } else if (error.message?.includes('already exists')) {
        errorMessage = 'A player with this name already exists in the room. Please choose a different name.';
      }
      
      setError(errorMessage);
      setLoading(false);
      return false;
    }
  };
  
  // Set up real-time subscription for room updates
  const setupRoomSubscription = useCallback(() => {
    console.log('Setting up real-time subscriptions for:', roomCode);
    
    // Clean up existing subscriptions to prevent duplicates
    cleanupSubscriptions();
    
    if (!roomCode) {
      console.log('No room code available, skipping subscription setup');
      return;
    }

    try {
      // Set up real-time subscription for room changes
      const roomSubscription = supabase
        .channel(`room-${roomCode}`)
        .on('postgres_changes', { 
          event: 'UPDATE', 
          schema: 'public', 
          table: 'game_rooms',
          filter: `room_code=eq.${roomCode}`
        }, (payload) => {
          console.log('🔄 Real-time update received for game room:', payload);
          
          // Immediately update critical state changes without a full fetch
          if (payload.new) {
            // Handle wheel spin state changes specifically
            setRoomData(prevData => {
              if (!prevData) return null;
              
              // Create a deep copy of the current state
              const updatedData = {
                ...prevData,
                is_wheel_spinning: payload.new.is_wheel_spinning,
                wheel_target_result: payload.new.wheel_target_result,
                wheel_target_player: payload.new.wheel_target_player,
                current_spin_result: payload.new.current_spin_result,
                current_spin_player: payload.new.current_spin_player,
                spin_timestamp: payload.new.spin_timestamp,
                spin_duration: payload.new.spin_duration,
                current_player_index: payload.new.current_player_index
              };
              
              console.log('Immediate state update:', {
                is_spinning: updatedData.is_wheel_spinning,
                target_result: updatedData.wheel_target_result,
                current_player_index: updatedData.current_player_index,
                prev_player_index: prevData.current_player_index
              });
              
              // Check if the player turn changed
              if (prevData.current_player_index !== updatedData.current_player_index) {
                const prevPlayerName = prevData.players && prevData.players[prevData.current_player_index] 
                  ? prevData.players[prevData.current_player_index].player_name 
                  : 'Unknown';
                const newPlayerName = updatedData.players && updatedData.players[updatedData.current_player_index] 
                  ? updatedData.players[updatedData.current_player_index].player_name 
                  : 'Unknown';
                console.log(`🔄 Turn changed: ${prevPlayerName} (${prevData.current_player_index}) → ${newPlayerName} (${updatedData.current_player_index})`);
              }
              
              return updatedData;
            });
            
            // Also trigger a full data refresh in the background to ensure consistency
            fetchRoomData(0, 0, false);
          }
        })
        .subscribe((status) => {
          console.log('Room subscription status:', status);
        });
      
      // Set up a more aggressive real-time player subscription for this room
      // Listen to all player changes and refresh the room data (simpler and more reliable)
      const playerSubscription = supabase
        .channel(`players-${roomCode}`)
        .on('postgres_changes', { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'players'
        }, (payload) => {
          console.log('👤 NEW PLAYER JOINED:', payload);
          // Always refresh room data when any player is added (we'll filter on the server side)
          console.log('👤 Player joined, refreshing data for all rooms');
          fetchRoomData(0, 0, false);
        })
        .on('postgres_changes', { 
          event: 'UPDATE', 
          schema: 'public', 
          table: 'players'
        }, (payload) => {
          console.log('👤 PLAYER UPDATED:', payload);
          // Always refresh room data when any player is updated
          console.log('👤 Player updated, refreshing data for all rooms');
          fetchRoomData(0, 0, false);
        })
        .on('postgres_changes', { 
          event: 'DELETE', 
          schema: 'public', 
          table: 'players'
        }, (payload) => {
          console.log('👤 PLAYER LEFT:', payload);
          // Always refresh room data when any player leaves
          console.log('👤 Player left, refreshing data for all rooms');
          fetchRoomData(0, 0, false);
        })
        .subscribe((status) => {
          console.log('Players subscription status:', status);
        });
      
      // Also subscribe to spin_results table for more reliable spin state
      const spinResultsSubscription = supabase
        .channel(`spin-results-${roomCode}`)
        .on('postgres_changes', { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'spin_results'
        }, (payload) => {
          console.log('🎯 NEW SPIN RESULT:', payload);
          fetchRoomData(0, 0, false);
        })
        .subscribe((status) => {
          console.log('Spin results subscription status:', status);
        });
      
      // Store subscription references
      roomSubscriptionRef.current = roomSubscription;
      playerSubscriptionRef.current = playerSubscription;
      spinResultsSubscriptionRef.current = spinResultsSubscription;
      
      console.log('✅ Real-time subscriptions set up successfully for room:', roomCode);
    } catch (err) {
      console.error('Error setting up real-time subscriptions:', err);
    }

    // Add a visibility change handler for reconnection
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Use a small delay to avoid immediate fetch on tab change
        setTimeout(() => {
          fetchRoomData(0, 0, false);
        }, 300);
      }
    };
    
    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Return cleanup function
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      if (roomSubscriptionRef.current) {
        roomSubscriptionRef.current.unsubscribe();
        roomSubscriptionRef.current = null;
      }
      
      if (playerSubscriptionRef.current) {
        playerSubscriptionRef.current.unsubscribe();
        playerSubscriptionRef.current = null;
      }
      
      if (spinResultsSubscriptionRef.current) {
        spinResultsSubscriptionRef.current.unsubscribe();
        spinResultsSubscriptionRef.current = null;
      }
    };
  }, [roomCode]); // Only depend on roomCode to prevent infinite loops
  
  // Set up subscriptions when we have room data
  useEffect(() => {
    if (roomCode && roomData) {
      console.log('Room data available, ensuring subscriptions are active');
      console.log('Current subscription status:', {
        roomSub: !!roomSubscriptionRef.current,
        playerSub: !!playerSubscriptionRef.current,
        spinResultsSub: !!spinResultsSubscriptionRef.current
      });
      
      // Always set up subscriptions to ensure they're fresh
      console.log('Setting up fresh subscriptions with room data available');
      setupRoomSubscription();
    }
  }, [roomCode, roomData, setupRoomSubscription]);

  // Add a polling fallback for real-time updates (in case subscriptions fail)
  useEffect(() => {
    if (!roomCode || !roomData) return;
    
    console.log('Setting up polling fallback for room updates');
    
    const pollingInterval = setInterval(() => {
      // Only poll if the tab is visible to save resources
      if (document.visibilityState === 'visible') {
        fetchRoomData(0, 0, false);
      }
    }, 10000); // Poll every 10 seconds (less frequent since subscriptions should work)
    
    return () => {
      console.log('Cleaning up polling fallback');
      clearInterval(pollingInterval);
    };
  }, [roomCode, roomData, fetchRoomData]);

  // Add a special effect to keep host connection alive
  useEffect(() => {
    let keepAliveInterval = null;
    
    // If this client is the host, set up a special keepalive
    if (isHost && roomData) {
      console.log('Setting up host keepalive ping');
      
      // Ping every 20 seconds to keep the connection alive if host
      keepAliveInterval = setInterval(() => {
        console.log('Host keepalive ping');
        
        // Simple ping that doesn't change data but keeps connection alive
        supabase
          .from('game_rooms')
          .select('id')
          .eq('room_code', roomCode)
          .single()
          .then(() => console.log('Host keepalive successful'))
          .catch(err => console.error('Host keepalive failed:', err));
      }, 20000);
    }
    
    // Clean up interval on unmount
    return () => {
      if (keepAliveInterval) {
        clearInterval(keepAliveInterval);
      }
    };
  }, [isHost, roomData, roomCode]);

  // Update wheel segments
  const updateWheelSegments = async (segments) => {
    try {
      const { error } = await supabase
        .from('game_rooms')
        .update({ wheel_segments: segments })
        .eq('room_code', roomCode);
        
      if (error) throw error;
      
      // Immediately update local state for instant feedback
      if (roomData) {
        setRoomData({
          ...roomData,
          wheel_segments: segments
        });
      }
      
      // Then fetch full updated data
      await fetchRoomData();
    } catch (err) {
      setError(err.message);
    }
  };

  // Update game settings
  const updateGameSettings = async (settings) => {
    try {
      const { error } = await supabase
        .from('game_rooms')
        .update({ game_settings: settings })
        .eq('room_code', roomCode);
        
      if (error) throw error;
      
      // Immediately update local state for instant feedback
      if (roomData) {
        setRoomData({
          ...roomData,
          game_settings: settings
        });
      }
      
      // Then fetch full updated data
      await fetchRoomData();
    } catch (err) {
      setError(err.message);
    }
  };

  // Improved spin wheel with proper turn management
  const spinWheel = async (result) => {
    try {
      // Only the current player or host can spin
      if (!canSpinWheel()) {
        console.warn('Attempted to spin when not allowed');
        return;
      }
      
      const spinTimestamp = new Date().toISOString();
      const spinDuration = 6; // 6 seconds for the wheel animation
      const postSpinDelay = 1; // 1 second delay after spin
      const resultDisplayDuration = 3; // 3 seconds to show result
      
      // Get current player info
      let currentPlayerName = '';
      let currentPlayerId = '';
      let currentPlayerIndex = 0;
      
      if (roomData && roomData.players && roomData.players.length > 0) {
        currentPlayerIndex = roomData.current_player_index >= 0 ? 
          roomData.current_player_index : 0;
          
        if (roomData.players[currentPlayerIndex]) {
          currentPlayerName = roomData.players[currentPlayerIndex].player_name;
          currentPlayerId = roomData.players[currentPlayerIndex].id;
        }
      }
      
      console.log(`🎰 Player ${currentPlayerName} (index ${currentPlayerIndex}) is spinning: ${result.text}`);
      
      // Phase 1: Start spinning - KEEP the same turn
      const updateData = {
        is_wheel_spinning: true,
        spin_timestamp: spinTimestamp,
        spin_duration: spinDuration,
        wheel_target_result: result.text,
        wheel_target_player: currentPlayerName,
        current_spin_result: null,
        current_spin_player: null
        // IMPORTANT: current_player_index is NOT changed here
      };
      
      await supabase
        .from('game_rooms')
        .update(updateData)
        .eq('room_code', roomCode);
      
      // Create spin result record
      await supabase
        .from('spin_results')
        .insert([{
          game_room_id: roomData.id,
          player_id: currentPlayerId || playerId,
          result_text: result.text,
          spin_timestamp: spinTimestamp
        }]);
      
      console.log('✅ Spin started, turn remains with current player during spin');
      
      // Only the current player/host manages the complete sequence to avoid conflicts
      const amITheSpinner = (isRoomHost() || (playerId && roomData.players[currentPlayerIndex]?.id === playerId));
      
             if (amITheSpinner) {
         console.log('🎮 Managing the complete spin sequence');
        
        // Show result after spin completes (add delay so users can see where wheel lands)
        setTimeout(async () => {
          console.log('🎯 Wheel animation complete, updating result');
          
          // Capture the result values at spin time to avoid closure issues
          const finalResultText = result.text;
          const finalPlayerName = currentPlayerName;
          
          console.log('🔧 Updating with captured values:', { finalResultText, finalPlayerName });
          
          // First update: Mark spin as complete but don't show popup yet
          await supabase
            .from('game_rooms')
            .update({ 
              is_wheel_spinning: false,
              wheel_target_result: finalResultText, // Keep the target result
              wheel_target_player: finalPlayerName
            })
            .eq('room_code', roomCode);
          
          // Second update after delay: Show result popup with SAME values
          setTimeout(async () => {
            console.log('🎉 Showing result popup after delay with same values:', { finalResultText, finalPlayerName });
            await supabase
              .from('game_rooms')
              .update({ 
                current_spin_result: finalResultText, // Use captured value
                current_spin_player: finalPlayerName  // Use captured value
              })
              .eq('room_code', roomCode);
          }, 2000); // 2 second delay to see where wheel landed
          
        }, spinDuration * 1000);
        
      } else {
        console.log('🔍 Not the spinner, waiting for sequence to complete');
      }
      
      // Broadcast to other tabs
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.postMessage({
          type: 'WHEEL_SPIN',
          result: result.text,
          spinTimestamp: spinTimestamp
        });
      }
      
    } catch (err) {
      console.error('Error spinning wheel:', err);
      setError('Error spinning wheel');
        }
  };

  // Function to go to next turn (called by button click)
  const nextTurn = async () => {
    try {
      if (!roomData || !roomData.players || roomData.players.length === 0) {
        console.error('No room data or players for turn change');
        return;
      }

      // Host can always advance turn, or current player can advance their own turn
      if (!isRoomHost() && !isMyTurn()) {
        console.warn('Only current player or host can advance turn');
        return;
      }

      console.log('🎯 Advancing to next turn');

      // Get fresh room data to calculate next player
      const { data: freshRoomData } = await supabase
        .from('game_rooms')
        .select('*, players(*)')
        .eq('room_code', roomCode)
        .single();
        
      if (freshRoomData && freshRoomData.players && freshRoomData.players.length > 0) {
        const currentIndex = freshRoomData.current_player_index || 0;
        const currentPlayerName = freshRoomData.players[currentIndex]?.player_name || 'Unknown';
        
        // Determine next player based on turn mode (default: sequential)
        const turnMode = freshRoomData.game_settings?.turnMode || 'sequential';
        let nextPlayerIndex;
        
        if (turnMode === 'random') {
          // Random mode: pick any player except current
          const availableIndices = freshRoomData.players
            .map((_, index) => index)
            .filter(index => index !== currentIndex);
          
          if (availableIndices.length > 0) {
            nextPlayerIndex = availableIndices[Math.floor(Math.random() * availableIndices.length)];
          } else {
            // Fallback if only one player
            nextPlayerIndex = currentIndex;
          }
        } else {
          // Sequential mode: next in list
          nextPlayerIndex = (currentIndex + 1) % freshRoomData.players.length;
        }
        
        const nextPlayerName = freshRoomData.players[nextPlayerIndex]?.player_name || 'Unknown';
        
        console.log(`🔄 Changing turn (${turnMode}): ${currentPlayerName} (${currentIndex}) → ${nextPlayerName} (${nextPlayerIndex})`);
        
        // Clear result and change turn
        await supabase
          .from('game_rooms')
          .update({ 
            // Clear all spin data
            is_wheel_spinning: false,
            current_spin_result: null,
            current_spin_player: null,
            wheel_target_result: null,
            wheel_target_player: null,
            spin_timestamp: null,
            spin_duration: null,
            // Change turn to next player
            current_player_index: nextPlayerIndex
          })
          .eq('room_code', roomCode);
          
        console.log(`✅ Turn successfully changed to player ${nextPlayerIndex}`);
      }
    } catch (err) {
      console.error('Error changing turn:', err);
      setError('Error changing turn');
    }
  };

  // Helper function to check if the current player can spin
  const canSpinWheel = useCallback(() => {
    try {
      if (!roomData) {
        console.log('Cannot spin: no room data');
        return false;
      }
      
      if (roomData.is_wheel_spinning) {
        console.log('Cannot spin: wheel is already spinning');
        return false;
      }
      
      if (!roomData.players || !Array.isArray(roomData.players) || roomData.players.length === 0) {
        console.log('Cannot spin: no players in room');
        return false;
      }
      
      // Solo play - host can always spin
      if (roomData.players.length === 1 && isRoomHost()) {
        return true;
      }
      
      // Check game settings
      const gameSettings = roomData.game_settings || {};
      
      // If only host can spin
      if (!gameSettings.allowPlayersToSpin) {
        return isRoomHost();
      }
      
      // Regular turn-based play
      return isMyTurn();
    } catch (err) {
      console.error('Error checking if can spin wheel:', err);
      return false;
    }
  }, [roomData, isRoomHost, isMyTurn]);

  // Leave room / cleanup - only called when intentionally leaving
  const leaveRoom = async () => {
    if (playerId && hasJoinedRef.current) {
      try {
        // Check if this player is the host before leaving
        const wasHost = isRoomHost();
        
        // Get current room data to find remaining players
        const { data: currentRoom } = await supabase
          .from('game_rooms')
          .select('*, players(*)')
          .eq('room_code', roomCode)
          .single();
        
        // Remove the leaving player
        await supabase
          .from('players')
          .delete()
          .eq('id', playerId);
        
        // Check if host is leaving
        if (wasHost && currentRoom) {
          const remainingPlayers = currentRoom.players.filter(p => p.id !== playerId);
          
          // Get room creator's user info
          const { data: roomCreator } = await supabase
            .from('users')
            .select('is_premium')
            .eq('id', currentRoom.host_user_id)
            .single();
          
          // If room becomes empty or host leaves (for free users), auto-delete the room
          if (remainingPlayers.length === 0 || (roomCreator && !roomCreator.is_premium)) {
            console.log('🗑️ Auto-deleting room - free user host left or room empty');
            
            // Mark room as inactive instead of deleting to preserve data
            await supabase
              .from('game_rooms')
              .update({ 
                is_active: false,
                updated_at: new Date().toISOString()
              })
              .eq('room_code', roomCode);
              
            console.log('✅ Room marked as inactive');
          } else if (remainingPlayers.length > 0) {
            // For premium users, transfer host to next player
            remainingPlayers.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
            const newHost = remainingPlayers[0];
            
            console.log(`👑 Transferring host from ${playerId} to ${newHost.id} (${newHost.player_name})`);
            
            // Update room with new host
            await supabase
              .from('game_rooms')
              .update({ 
                host_player_id: newHost.id,
                updated_at: new Date().toISOString()
              })
              .eq('room_code', roomCode);
              
            console.log(`✅ Host successfully transferred to ${newHost.player_name}`);
          }
        } else if (currentRoom && currentRoom.players.length === 1) {
          // If this is the last remaining player (not host), check if we should delete the room
          const { data: roomCreator } = await supabase
            .from('users')
            .select('is_premium')
            .eq('id', currentRoom.host_user_id)
            .single();
            
          if (roomCreator && !roomCreator.is_premium) {
            console.log('🗑️ Auto-deleting room - last player leaving free user room');
            
            await supabase
              .from('game_rooms')
              .update({ 
                is_active: false,
                updated_at: new Date().toISOString()
              })
              .eq('room_code', roomCode);
              
            console.log('✅ Room marked as inactive');
          }
        }
          
        // Reset joined status
        hasJoinedRef.current = false;
        localStorage.removeItem(`player_${roomCode}`);
      } catch (err) {
        console.error('Error leaving room:', err);
      }
    }
    
    // Clean up subscriptions
    cleanupSubscriptions();
  };

  // Explicit function to leave game
  const exitGame = async () => {
    await leaveRoom();
    
    // Clear local state
    setPlayerId(null);
    setIsHost(false);
    setRoomData(null);
  };

  // Initial fetch of room data only if roomCode is provided
  useEffect(() => {
    if (roomCode) {
      // Add a loading timeout to prevent infinite loading
      const loadingTimeout = setTimeout(() => {
        console.log('Loading timeout reached, forcing state update');
        setLoading(false);
      }, 10000); // 10 second timeout for loading
      
      // Fetch initial data
      fetchRoomData().then(success => {
        // Clear the timeout since we got a response
        clearTimeout(loadingTimeout);
        
        // Don't wait for subscription setup to complete loading
        if (success) {
          // Set up subscriptions in the background
          setupRoomSubscription();
        }
      }).catch(err => {
        // Clear the timeout and ensure loading is false if there's an error
        clearTimeout(loadingTimeout);
        console.error('Error in initial data fetch:', err);
        setLoading(false);
      });
      
      // Clean up subscriptions when room code changes
      return () => {
        cleanupSubscriptions();
      };
    }
  }, [roomCode]); // Only depend on roomCode to prevent infinite loops

  // Separate effect to handle cleanup on window close
  useEffect(() => {
    // Handle beforeunload event for browser close/refresh
    const handleBeforeUnload = () => {
      if (playerId && hasJoinedRef.current) {
        // We can't use async here, so we make a sync request
        supabase
          .from('players')
          .delete()
          .eq('id', playerId);
      }
    };
    
    // Add beforeunload event to handle browser close/refresh
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Clean up event listener on unmount
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [playerId]);

  // Effect to handle window close and disconnections
  useEffect(() => {
    if (!playerId || !roomCode) return;
    
    // Update connection status to online when component mounts
    const updateConnectionStatus = async (isConnected) => {
      try {
        const lastActive = new Date().toISOString();
        
        // First check if player still exists in database
        const { data: existingPlayer, error: checkError } = await supabase
          .from('players')
          .select('id')
          .eq('id', playerId)
          .single();
          
        // If player doesn't exist anymore, we need to create a new one or clear local storage
        if (checkError || !existingPlayer) {
          console.warn('Player no longer exists in database, clearing local state');
          localStorage.removeItem(`player_${roomCode}`);
          setPlayerId(null);
          hasJoinedRef.current = false;
          return;
        }
        
        // Update connection status
        await supabase
          .from('players')
          .update({ 
            is_connected: isConnected,
            last_active: lastActive
          })
          .eq('id', playerId);
          
        console.log(`Player connection status set to: ${isConnected ? 'connected' : 'disconnected'}`);
      } catch (err) {
        console.error('Error updating connection status:', err);
      }
    };
    
    // Set as connected when component mounts
    updateConnectionStatus(true);
    
    // Set up periodic heartbeat to update last_active timestamp
    const heartbeatInterval = setInterval(() => {
      if (document.visibilityState === 'visible' && playerId) {
        updateConnectionStatus(true);
      }
    }, 15000); // Every 15 seconds
    
    // Handle beforeunload event for browser close/refresh
    const handleBeforeUnload = () => {
      // We can't use async here, so we use a synchronous request
      navigator.sendBeacon(
        `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/players?id=eq.${playerId}`, 
        JSON.stringify({ 
          is_connected: false,
          last_active: new Date().toISOString()
        })
      );
    };
    
    // Handle visibility change events (tab switching, etc.)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updateConnectionStatus(true);
        // Also fetch room data when tab becomes visible
        fetchRoomData(0, 1, false);
      } else {
        updateConnectionStatus(false);
      }
    };
    
    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Clean up event listeners on unmount
    return () => {
      updateConnectionStatus(false);
      clearInterval(heartbeatInterval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [playerId, roomCode, fetchRoomData]);

  // Set up broadcast channel for cross-tab communication
  useEffect(() => {
    // Only setup if BroadcastChannel is supported
    if (window.BroadcastChannel && roomCode) {
      try {
        // Create a broadcast channel for this room
        const channelName = `game-room-${roomCode}`;
        broadcastChannelRef.current = new BroadcastChannel(channelName);
        
        // Listen for messages from other tabs
        broadcastChannelRef.current.onmessage = (event) => {
          if (event.data.type === 'PLAYER_JOINED' || 
              event.data.type === 'PLAYER_LEFT' || 
              event.data.type === 'WHEEL_SPIN') {
            console.log('📢 Received cross-tab message:', event.data);
            // Refresh data when another tab reports changes
            fetchRoomData(0, 0, false);
          }
        };
        
        console.log('📢 BroadcastChannel set up for room:', roomCode);
      } catch (err) {
        console.error('Error setting up BroadcastChannel:', err);
      }
    }
    
    // Clean up on unmount
    return () => {
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.close();
        broadcastChannelRef.current = null;
      }
    };
  }, [roomCode, fetchRoomData]);

  return {
    roomData,
    loading: loading || isRecovering,
    error,
    setError,
    playerId,
    createRoom,
    joinRoom,
    updateWheelSegments,
    updateGameSettings,
    spinWheel,
    nextTurn,
    isMyTurn,
    isRoomHost,
    canSpinWheel,
    exitGame,
    fetchRoomData,
    attemptRecovery,
    isRecovering
  };
};

export default useGameRoom; 